import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';

import { loadMobileAppStrings } from './api';
import { hasTx, tx, useXomicsI18n } from './i18n';

// The phone the portal draws is the Phenome Longevity app itself: its layout,
// colours, icons and its own words, read from the app's catalogue in the
// viewer's language. What a person saw is rebuilt from the screens their
// session touched; the numbers on those screens are the app's layout, not
// that person's data. The screens themselves are drawn in AppScreens.js.

export const APP_BLUE = '#203a85';
export const SLEEP = '#bb5ae0';
export const CALORIES = '#ff8d28';
export const STEPS = '#34c759';
export const CAPACITY = '#ffcc00';

// ---------------------------------------------------------------- the words

const appStrings = new Map();
const pendingStrings = new Map();

// One request per language, shared by every phone on the page.
function stringsFor(language) {
  if (!pendingStrings.has(language)) {
    pendingStrings.set(language, loadMobileAppStrings(language).then((answer) => {
      appStrings.set(language, answer.strings);
      return answer.strings;
    }).finally(() => pendingStrings.delete(language)));
  }
  return pendingStrings.get(language);
}

export function useAppStrings() {
  const { language } = useXomicsI18n();
  const [strings, setStrings] = useState(() => (appStrings.has(language) ? appStrings.get(language) : null));
  useEffect(() => {
    if (appStrings.has(language)) {
      setStrings(appStrings.get(language));
      return undefined;
    }
    let live = true;
    stringsFor(language)
      .then((loaded) => { if (live) setStrings(loaded); })
      .catch(() => { if (live) setStrings({}); });
    return () => { live = false; };
  }, [language]);
  return useMemo(() => (key, variables) => {
    if (strings === null) return '';
    const text = typeof strings[key] === 'string' ? strings[key] : `⟦${key}⟧`;
    if (!variables) return text;
    return text.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (match, name) => (Object.hasOwn(variables, name) ? String(variables[name]) : match));
  }, [strings]);
}

// ---------------------------------------------------------------- the screens a session touched

// A route is what a screen asked the server for, in the order checked; the
// first match wins. Builds before 5.4 are known only by these.
const ROUTE_SCREENS = [
  [/^\/(Auth\/(check-config|me|refresh)|Account\/configuration|Language\/|Notifications\/device-push-token)/, 'launch'],
  [/^\/(Shop\/(places|quote)|Auth\/address)/, 'checkout'],
  [/^\/Auth\//, 'signin'],
  [/^\/(Health\/(Query|AddWaterIntake|Goals\/Set)|Nutrition\/(meals\/range|goal)|Habits\/(range|list)|Activity\/history|Bookings\/upcoming)$/, 'home'],
  [/^\/Nutrition\//, 'diet'],
  [/^\/Habits\//, 'habits'],
  [/^\/Activity\//, 'activity'],
  [/^\/Patient\/reports\//, 'reports'],
  [/^\/Patient\//, 'insights'],
  [/^\/Clinical\//, 'clinical'],
  [/^\/AI\//, 'ai'],
  [/^\/Shop\/(orders|track)/, 'orders'],
  [/^\/Shop\//, 'store'],
  [/^\/Bookings\//, 'booking'],
  [/^\/Games\//, 'games'],
  [/^\/Collections\//, 'tests'],
  [/^\/Account\//, 'profile'],
];

// What the app logs under a bare name: the screen it happened on, how to say
// it, and the part of that screen when the screen has several.
const EVENTS = {
  HomeNavigationPress: ['home', 'ui.mobile.event_home'],
  InsightsNavigationPress: ['insights', 'ui.mobile.event_insights'],
  LongevityAINavigationPress: ['ai', 'ui.mobile.event_longevity_ai', 'chat'],
  StoreNavigationPress: ['store', 'ui.mobile.event_store', 'store'],
  ProfileNavigationPress: ['profile', 'ui.mobile.event_profile', null],
  AiChatMessage: ['ai', 'ui.mobile.event_ai_message', 'chat'],
  AiHistoryOpen: ['ai', 'ui.mobile.event_ai_history', 'chat'],
  AiVoiceOpen: ['ai', 'ui.mobile.event_ai_voice', 'chat'],
  SettingsOpen: ['profile', 'ui.mobile.event_settings', null],
  LogoutPress: ['profile', 'ui.mobile.event_logout', null],
  PastTestsOpen: ['tests', 'ui.mobile.event_past_tests'],
  ClinicalInsightsOpen: ['clinical', 'ui.mobile.event_clinical'],
  ClinicalValueAdd: ['clinical', 'ui.mobile.event_clinical_value'],
  ClinicalLabReportRead: ['clinical', 'ui.mobile.event_lab_read'],
  ClinicalLabReportSaved: ['clinical', 'ui.mobile.event_lab_saved'],
  ShopAddToCart: ['store', 'ui.mobile.event_add_to_cart', 'product'],
  ShopCheckoutStart: ['checkout', 'ui.mobile.event_checkout'],
  ShopPaymentSuccess: ['checkout', 'ui.mobile.event_paid'],
  OrderHistoryOpen: ['orders', 'ui.mobile.event_orders'],
  BookConsultationPress: ['booking', 'ui.mobile.event_booking', 'flow_time'],
  BookingFlowOpen: ['booking', 'ui.mobile.event_booking', 'clinic_flow'],
  BookingConfirmed: ['booking', 'ui.mobile.event_booked', 'clinic_flow'],
  DevicePaired: ['device', 'ui.mobile.event_device_paired', 'paired'],
  HabitToggle: ['habits', 'ui.mobile.event_habit', 'list'],
  ActivityStart: ['activity', 'ui.mobile.event_activity_start', 'timer'],
  ActivityFinish: ['activity', 'ui.mobile.event_activity_finish', 'timer'],
};

// Calls that are something a person did rather than a screen loading.
const ROUTE_ACTIONS = {
  '/AI/text': 'ui.mobile.step_ai_text',
  '/AI/voice/session': 'ui.mobile.step_ai_voice',
  '/AI/photo': 'ui.mobile.step_ai_photo',
  '/AI/conversations/:id': 'ui.mobile.step_ai_conversation',
  '/Shop/product': 'ui.mobile.step_product',
  '/Shop/quote': 'ui.mobile.step_quote',
  '/Patient/reports/create': 'ui.mobile.step_report_create',
  '/Patient/reports/download': 'ui.mobile.step_report_download',
  '/Patient/data-fetch': 'ui.mobile.step_results',
  '/Habits/toggle': 'ui.mobile.event_habit',
  '/Activity/start': 'ui.mobile.event_activity_start',
  '/Activity/finish': 'ui.mobile.event_activity_finish',
  '/Nutrition/items/add': 'ui.mobile.step_food_add',
  '/Nutrition/foods/search': 'ui.mobile.step_food_search',
  '/Health/AddWaterIntake': 'ui.mobile.step_water',
  '/Health/Goals/Set': 'ui.mobile.step_goal',
  '/Bookings/book': 'ui.mobile.event_booked',
  '/Account/password': 'ui.mobile.step_password',
  '/Account/delete': 'ui.mobile.step_account_delete',
  '/Account/profile': 'ui.mobile.step_profile_saved',
  '/Account/health-questionnaire': 'ui.mobile.step_questionnaire',
  '/Collections/validate-activation': 'ui.mobile.step_kit_code',
  '/Collections/finalize-activation': 'ui.mobile.step_kit_activated',
  '/Auth/login': 'ui.mobile.step_signed_in',
  '/Auth/oauth/:provider': 'ui.mobile.step_signed_in',
  '/Auth/login-otp/confirm': 'ui.mobile.step_signed_in',
  '/Auth/registration/complete': 'ui.mobile.step_signed_up',
  '/Auth/invitation/accept': 'ui.mobile.step_invitation',
  '/Auth/logout': 'ui.mobile.event_logout',
};

const METRIC_PRESS = /^([a-zA-Z]+)MetricPress$/;
const NAV_PRESS = /^([A-Za-z]+)NavigationPress$/;

// From 5.4 the app names each screen as it opens (Open.<Screen>) and each tap
// (<Area>.<Action>, and after one more dot the value it was about). A screen
// comes with its part where the app has several: the page of a flow, the
// metric of a health chart, the omics section.
const OPENED = {
  Habits: ['habits', 'list'], HabitDetail: ['habits', 'detail'],
  Nutrition: ['diet', 'tracker'], AddMeal: ['diet', 'add'], MealDetail: ['diet', 'meal'], NutritionGoal: ['diet', 'goal'],
  Activity: ['activity', 'list'], ActivityDetail: ['activity', 'detail'], ActivityPicker: ['activity', 'picker'], ActivityTimer: ['activity', 'timer'],
  Capacity: ['metric', 'capacity'], Games: ['games', 'list'],
  Consultations: ['booking', 'list'], ConsultBooking: ['booking', 'flow'], ConsultDetail: ['booking', 'detail'], ConsultPast: ['booking', 'past'],
  ConsultRating: ['booking', 'rating'], VideoCall: ['booking', 'call'], ClinicBookings: ['booking', 'clinic'],
  YourDevices: ['device', 'list'], DeviceDetail: ['device', 'detail'], AddDevice: ['device', 'add'], DeviceScan: ['device', 'scan'], DeviceFound: ['device', 'found'],
  Genome: ['omics', 'genome'], GenomeDiseases: ['omics', 'genome'], GenomeDrugs: ['omics', 'genome'],
  GenomeTraits: ['omics', 'genome'], GenomePanel: ['omics', 'genome'],
  CarrierScreening: ['omics', 'carrier'], NewbornScreening: ['omics', 'newborn'], SportsPerformance: ['omics', 'sports'],
  Proteomics: ['omics', 'proteome'], Transcriptome: ['omics', 'transcriptome'], Lipidome: ['omics', 'lipidome'],
  UrineMetabolome: ['omics', 'urine'], PlasmaMetabolome: ['omics', 'plasma'],
  GutHealth: ['omics', 'gut'], GutRisk: ['omics', 'gut'], OralHealth: ['omics', 'oral'], OralRisk: ['omics', 'oral'],
  ReportInbox: ['reports', 'inbox'], PdfViewer: ['reports', 'viewer'],
  LongevityAI: ['ai', 'chat'], AiHistory: ['ai', 'history'], VoiceAssistant: ['ai', 'voice'],
  Store: ['store', 'store'], ProductDetail: ['store', 'product'], Cart: ['store', 'cart'], CheckoutAddress: ['checkout'], Profile: ['profile', null],
};

// A category's list of results (Open.OmicsReports_<category>) opens before its
// omics section. The genome category arrives in the app's own words.
const RESULT_SECTIONS = {
  WholeGenome: 'genome', GenomeInsights: 'genome', GutMicrobiomeTest: 'gut', OralMicrobiomeTest: 'oral',
  Transcriptome: 'transcriptome', Proteome: 'proteome', Proteomics: 'proteome', Lipidome: 'lipidome',
  Metabolome: 'metabolome', UrineMetabolome: 'urine', PlasmaMetabolome: 'plasma',
  PrecisionOncology: 'oncology', InfectiousDiseases: 'infectious',
};

// The part a screen shows when nothing names one: the page it opens on.
const FIRST_PARTS = {
  habits: 'list', diet: 'tracker', activity: 'list', games: 'list', booking: 'list', device: 'list',
  store: 'store', ai: 'chat', reports: 'inbox',
};

// The screen a tap happened on, by its area. With no part given the tap
// belongs to whichever part is open; App and Push stay on the open screen.
const AREAS = {
  App: [null], Push: [null], Auth: ['signin'], Register: ['signin'], Home: ['home'], Health: ['metric'],
  Habits: ['habits', 'list'], Nutrition: ['diet', 'tracker'], Activity: ['activity', 'list'], Games: ['games'],
  Consult: ['booking', 'flow'], Booking: ['booking', 'clinic_flow'], Devices: ['device'], Insights: ['insights'],
  Genome: ['omics', 'genome'], Carrier: ['omics', 'carrier'], Newborn: ['omics', 'newborn'], Sports: ['omics', 'sports'],
  Proteomics: ['omics', 'proteome'], Transcriptome: ['omics', 'transcriptome'], Lipidome: ['omics', 'lipidome'],
  Metabolome: ['omics'], Gut: ['omics', 'gut'], Oral: ['omics', 'oral'],
  Clinical: ['clinical'], Reports: ['reports', 'request'], AI: ['ai', 'chat'], Store: ['store', 'store'], Cart: ['store', 'cart'],
  Checkout: ['checkout'], Profile: ['profile'], Settings: ['profile'], Kit: ['profile', 'activate_test'], Orders: ['orders'], Tests: ['tests'],
};

// Taps on another page than their area's first one.
const PLACES = {
  'Habits.AddOpen': ['habits', 'add'], 'Habits.AddSubmit': ['habits', 'add'], 'Habits.CompleteFromDetail': ['habits', 'detail'],
  'Habits.EditOpen': ['habits', 'edit'], 'Habits.EditSave': ['habits', 'edit'], 'Habits.DeleteTap': ['habits', 'edit'],
  'Habits.DeleteConfirm': ['habits', 'edit'],
  'Nutrition.GoalSave': ['diet', 'goal'], 'Nutrition.FoodSearchOpen': ['diet', 'add'], 'Nutrition.FoodSearch': ['diet', 'add'],
  'Nutrition.FoodPick': ['diet', 'add'], 'Nutrition.FoodClear': ['diet', 'add'], 'Nutrition.ScanMealTap': ['diet', 'add'],
  'Nutrition.ScanMealCapture': ['diet', 'add'], 'Nutrition.NutrientsOpen': ['diet', 'add'], 'Nutrition.AddFoodSubmit': ['diet', 'add'],
  'Nutrition.FoodOpen': ['diet', 'meal'], 'Nutrition.AddMoreFood': ['diet', 'meal'], 'Nutrition.MealNutrientsOpen': ['diet', 'meal'],
  'Nutrition.FoodDeleteTap': ['diet', 'meal'], 'Nutrition.FoodDeleteConfirm': ['diet', 'meal'], 'Nutrition.FoodNutrientsOpen': ['diet', 'meal'],
  'Activity.Pick': ['activity', 'picker'], 'Activity.Search': ['activity', 'picker'], 'Activity.FavoriteOn': ['activity', 'picker'],
  'Activity.FavoriteOff': ['activity', 'picker'],
  'Consult.BookTap': ['booking', 'list'], 'Consult.PastOpen': ['booking', 'list'], 'Consult.DetailOpen': ['booking', 'list'],
  'Consult.ContinueToTime': ['booking', 'flow_time'], 'Consult.BackToQuestions': ['booking', 'flow'],
  'Consult.DatePick': ['booking', 'flow_time'], 'Consult.SlotPick': ['booking', 'flow_time'], 'Consult.MonthBack': ['booking', 'flow_time'],
  'Consult.MonthForward': ['booking', 'flow_time'], 'Consult.ConfirmTap': ['booking', 'flow_time'], 'Consult.JoinCall': ['booking', 'detail'], 'Consult.PastDetailOpen': ['booking', 'past'], 'Consult.RatingStars': ['booking', 'rating'],
  'Consult.RatingSubmit': ['booking', 'rating'], 'Consult.RatingSkip': ['booking', 'rating'], 'Consult.CallEnd': ['booking', 'call'],
  'Booking.StartTap': ['booking', 'clinic'],
  'Devices.AddTap': ['device', 'list'], 'Devices.DetailOpen': ['device', 'list'], 'Devices.Pick': ['device', 'add'],
  'Devices.ScanStart': ['device', 'scan'], 'Devices.ChooseDismissed': ['device', 'scan'], 'Devices.NoneFound': ['device', 'scan'],
  'Devices.PermissionDenied': ['device', 'scan'], 'Devices.PermissionRetry': ['device', 'scan'], 'Devices.ScanError': ['device', 'scan'],
  'Devices.FoundOne': ['device', 'scan'], 'Devices.FoundSeveral': ['device', 'scan'],
  'Devices.PairTap': ['device', 'found'], 'Devices.NotMine': ['device', 'found'], 'Devices.PairFailed': ['device', 'found'],
  'Devices.SyncTap': ['device', 'detail'], 'Devices.ForgetTap': ['device', 'detail'], 'Devices.ForgetConfirm': ['device', 'detail'],
  'Devices.PriorityOn': ['device', 'detail'], 'Devices.PriorityOff': ['device', 'detail'],
  'Insights.ReportOpen': ['results'], 'Insights.ReportLockedTap': ['results'], 'Insights.ReportsBack': ['results'],
  'Insights.ReportsNext': ['results'],
  'Reports.Open': ['reports', 'inbox'], 'Reports.PendingTap': ['reports', 'inbox'], 'Reports.PdfFastScroll': ['reports', 'viewer'],
  'AI.VoiceClose': ['ai', 'voice'], 'AI.VoiceSpeakerToggle': ['ai', 'voice'], 'AI.VoiceMute': ['ai', 'voice'], 'AI.VoiceUnmute': ['ai', 'voice'],
  'Store.RemoveFromCart': ['store', 'product'], 'Store.CartOpenFromProduct': ['store', 'product'], 'Store.ProductImage': ['store', 'product'],
  'Profile.PersonalDetailsSave': ['profile', 'personal_details'], 'Profile.DeleteAccountTap': ['profile', 'personal_details'],
  'Profile.HealthHistorySave': ['profile', 'health_history'], 'Profile.LifestyleSave': ['profile', 'lifestyle'],
  'Profile.PasswordChangeSubmit': ['profile', 'change_password'], 'Profile.AddressPick': ['profile', 'saved_address'],
  'Profile.AddressSave': ['profile', 'saved_address'], 'Settings.Appearance': ['profile', 'appearance'], 'Settings.Language': ['profile', 'language'],
};

// Taps that bring up a page the app does not name as it opens: the press is
// made on the screen before, and the page it opens is where the person is next.
const OPENS = new Set([
  'Habits.AddOpen', 'Habits.EditOpen', 'Games.LeaderboardOpen', 'Games.Play', 'AI.ConversationOpen',
  'Consult.ContinueToTime', 'Consult.BackToQuestions', 'PastTestsOpen', 'OrderHistoryOpen', 'ClinicalInsightsOpen',
]);

// Taps whose value changes what they say, and how that value is named. The
// label itself is ui.mobile.ev_<area>_<action> in the catalogue; a variant has
// one label per value (ui.mobile.ev_auth_sign_in_google).
const TAP_DETAILS = {
  'Auth.SignIn': 'variant', 'Register.Step': 'variant', 'Consult.Type': 'variant', 'Consult.Clinician': 'variant',
  'Insights.Metabolome': 'variant', 'Genome.TraitPanelTap': 'variant',
  'Health.MetricSwitch': 'metric', 'Health.ChartScrub': 'metric', 'Health.GoalOpen': 'metric', 'Health.GoalSave': 'metric',
  'Health.Period': 'period', 'Habits.Range': 'period', 'Nutrition.Range': 'period', 'Activity.Range': 'period',
  'Nutrition.AddMealOpen': 'meal', 'Nutrition.MealOpen': 'meal', 'Activity.Pick': 'activity',
  'Games.LeaderboardOpen': 'game', 'Games.Play': 'game', 'Games.Finish': 'game', 'Games.PlayAgain': 'game',
  'Devices.Pick': 'device', 'Devices.DetailOpen': 'device', 'Insights.Open': 'omics', 'Genome.Panel': 'panel',
  'Booking.Type': 'words', 'Booking.Tier': 'words', 'Genome.ViewAll': 'words',
  'Settings.Appearance': 'appearance', 'Settings.Language': 'language',
  'Consult.RatingStars': 'count', 'Booking.Duration': 'count', 'AI.SuggestedPrompt': 'count',
};
const PERIODS = {
  day: 'health.detail.period.daily', week: 'health.detail.period.weekly', month: 'health.detail.period.monthly',
  sixMonth: 'health.detail.period.six_month', sixMonths: 'health.detail.period.six_month', year: 'health.detail.period.yearly',
};
const DEVICES = { ring: 'device.ring', strap: 'device.band', watch: 'device.watch', yc_ring: 'device.ring', qc_band: 'device.band' };
const OMICS_TAPPED = {
  ClinicalInsights: 'omics.clinical.title', WholeGenome: 'omics.genome.title', Metabolome: 'omics.metabolome.title',
  GutMicrobiomeTest: 'omics.gut.title', OralMicrobiomeTest: 'omics.oral.title', Transcriptome: 'omics.transcriptome.title',
  Proteome: 'omics.proteome.title', Proteomics: 'omics.proteome.title', Lipidome: 'omics.lipidome.title',
  PrecisionOncology: 'omics.oncology.title', InfectiousDiseases: 'omics.infectious.title',
};
const PANELS = {
  HealthConditions: 'genomics.panels.health_conditions', CarrierRisk: 'genomics.panels.carrier_risk',
  Newborn: 'genomics.panels.newborn', HereditaryCancer: 'genomics.panels.hereditary_cancer',
};
// Profile rows report Profile.<Row>; each reads "Opened <the row's name>" and
// opens its own page.
const PROFILE_ROWS = {
  HealthHistory: 'profile.menu.health_history', Lifestyle: 'profile.menu.lifestyle', ChangePassword: 'profile.menu.change_password',
  SavedAddress: 'profile.menu.saved_address', Appearance: 'profile.menu.appearance', Language: 'profile.menu.language',
  Privacy: 'profile.menu.privacy', ActivateTest: 'profile.menu.activate_test', Faqs: 'profile.menu.faqs',
  PersonalDetails: 'profile.menu.personal_details',
};

const snake = (value) => value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2').toLowerCase();
const words = (value) => value.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();

// An event name as area, action and value; "RatingStars5" carries its value as digits.
function tapParts(name) {
  const [area, action = '', ...rest] = name.split('.');
  const digits = /^([A-Za-z]+?)(\d+)$/.exec(action);
  if (digits && TAP_DETAILS[`${area}.${digits[1]}`] === 'count') return { area, action: digits[1], value: digits[2] };
  return { area, action, value: rest.length > 0 ? rest.join('.') : null };
}

function languageName(code) {
  try {
    return new Intl.DisplayNames(undefined, { type: 'language' }).of(code.toLowerCase()) || code;
  } catch {
    return code;
  }
}

function valueName(kind, value, as) {
  const named = (key) => (key ? as(key) : words(value));
  switch (kind) {
    case 'metric': return metricName(value, as);
    case 'period': return named(PERIODS[value]);
    case 'meal': return as(`nutrition.${value.toLowerCase()}`);
    case 'activity': return as(`activity.type.${value}`);
    case 'game': return as(`games.${value}`);
    case 'device': return named(DEVICES[value]);
    case 'omics': return named(OMICS_TAPPED[value]);
    case 'panel': return named(PANELS[value]);
    case 'appearance': return as(`profile.appearance.${value}`);
    case 'language': return languageName(value);
    case 'count': return value;
    default: return words(value);
  }
}

// What a 5.4 tap says, or null when the catalogue has no label for it.
function tapLabel(name, as) {
  const { area, action, value } = tapParts(name);
  if (area === 'Profile' && PROFILE_ROWS[action]) return tx('ui.mobile.ev_profile_open', { detail: as(PROFILE_ROWS[action]) });
  const key = `ui.mobile.ev_${snake(area)}_${snake(action)}`;
  const kind = TAP_DETAILS[`${area}.${action}`];
  if (kind === 'variant') return value !== null && hasTx(`${key}_${snake(value)}`) ? tx(`${key}_${snake(value)}`) : null;
  if (!hasTx(key)) return null;
  if (kind === undefined) return tx(key);
  return value === null ? null : tx(key, { detail: valueName(kind, value, as) });
}

// A part left unnamed is the page the screen opens on.
function placed(screen, part, opens = false) {
  return { screen, part: part === null && FIRST_PARTS[screen] ? FIRST_PARTS[screen] : part, opens };
}

// Where a step happened. `screen` null keeps the screen that is open; `part`
// undefined keeps the part that is open. `opens` marks the press that brought
// the screen up, made on the screen before it.
export function screenOfStep(step) {
  if (step.kind !== 'event') {
    const found = ROUTE_SCREENS.find(([pattern]) => pattern.test(step.name));
    return placed(found ? found[1] : 'home', null);
  }
  const pressed = METRIC_PRESS.exec(step.name);
  if (pressed) return placed('metric', pressed[1], true);
  if (EVENTS[step.name]) {
    const [screen, , part] = EVENTS[step.name];
    return placed(screen, part, NAV_PRESS.test(step.name) || OPENS.has(step.name));
  }
  // The app opens on its Home tab, which reports nothing else when it shows.
  if (!step.name.includes('.') || step.name === 'App.Open') return placed('home', null);
  const { area, action, value } = tapParts(step.name);
  if (area === 'Open') {
    const named = /^(Health|OmicsReports|PdfRequest)_(.*)$/.exec(action);
    if (named && named[1] === 'Health') return placed('metric', named[2]);
    if (named && named[1] === 'OmicsReports') return placed('results', RESULT_SECTIONS[named[2]] || null);
    if (named) return placed('reports', 'request');
    return OPENED[action] ? placed(OPENED[action][0], OPENED[action].length > 1 ? OPENED[action][1] : null) : { screen: null };
  }
  const tap = `${area}.${action}`;
  if (area === 'Profile' && PROFILE_ROWS[action]) return placed('profile', snake(action), true);
  if (area === 'Games' && value !== null) {
    return placed('games', `${action === 'LeaderboardOpen' ? 'board' : 'play'}.${value}`, OPENS.has(tap));
  }
  if (TAP_DETAILS[tap] === 'metric' && value !== null) return placed('metric', value);
  const place = PLACES[tap] || AREAS[area];
  if (place === undefined) return { screen: null };
  return placed(place[0], place.length > 1 ? place[1] : undefined, OPENS.has(tap));
}

// App lifecycle moments rather than something a person did.
export function isLifecycle(step) {
  return step.kind === 'event' && step.name.startsWith('App.');
}

// What a step says in the timeline, or null when it is only a screen loading.
export function stepLabel(step, as) {
  if (step.kind === 'event') {
    const pressed = METRIC_PRESS.exec(step.name);
    if (pressed) return tx('ui.mobile.step_metric', { metric: metricName(pressed[1], as) });
    if (EVENTS[step.name]) return tx(EVENTS[step.name][1]);
    // A screen appearing, and the app opening, are already the visit itself.
    if (step.name.startsWith('Open.') || step.name === 'App.Open') return null;
    const label = step.name.includes('.') ? tapLabel(step.name, as) : null;
    return label !== null ? label : step.name.replace(/\./g, ' · ').replace(/([a-z])([A-Z])/g, '$1 $2');
  }
  return ROUTE_ACTIONS[step.name] ? tx(ROUTE_ACTIONS[step.name]) : null;
}

// Where on the drawn phone a step was pressed: the data-tap marks of that spot,
// the first one the screen has winning, or null when the step was no press.
const TAP_TARGETS = {
  AiChatMessage: 'ai.send', AiHistoryOpen: 'ai.history', AiVoiceOpen: 'ai.voice', LogoutPress: 'profile.logout',
  PastTestsOpen: 'profile.menu.past_tests', OrderHistoryOpen: 'profile.menu.order_history', ClinicalInsightsOpen: 'insights.clinical',
  ClinicalValueAdd: 'clinical.values', ClinicalLabReportRead: 'clinical.upload', ShopAddToCart: 'store.add', HabitToggle: 'habits.check',
  ActivityStart: 'activity.start', ActivityFinish: 'activity.start', BookConsultationPress: 'booking.book', BookingConfirmed: 'booking.confirm',
  'Home.AddDeviceTap': 'home.device.add', 'Home.DeviceTileTap': ['home.device.tile', 'home.device.add'],
  'Home.AllDevicesTap': ['home.device.all', 'home.device.add'], 'Home.CapacityTap': 'home.capacity',
  'Home.ActivityCardTap': 'home.activity', 'Home.ActivityAddTap': 'home.activity.add', 'Home.DietCardTap': 'home.diet',
  'Home.HabitsCardTap': 'home.habits', 'Home.ConsultationBookTap': 'home.consult.book', 'Home.ConsultationsTap': 'home.consult',
  'Home.GamesTap': 'home.games', 'Home.DayBack': 'home.day.back', 'Home.DayForward': 'home.day.forward',
  'Health.ChartScrub': 'metric.chart', 'Health.MetricPickerOpen': 'metric.picker', 'Health.MetricSwitch': 'metric.picker',
  'Health.GoalOpen': 'metric.goal', 'Health.GoalSave': 'metric.goal', 'Health.DateBack': 'range.back', 'Health.DateForward': 'range.forward',
  'Habits.AddOpen': 'habits.add', 'Habits.DetailOpen': 'habits.detail', 'Habits.CompleteFromDetail': 'habits.complete',
  'Habits.EditOpen': 'habits.edit', 'Habits.AddSubmit': 'habits.submit', 'Habits.EditSave': 'habits.save',
  'Habits.DeleteTap': 'habits.delete', 'Habits.DeleteConfirm': 'habits.delete',
  'Habits.RangeBack': 'range.back', 'Habits.RangeForward': 'range.forward', 'Nutrition.RangeBack': 'range.back',
  'Nutrition.RangeForward': 'range.forward', 'Activity.RangeBack': 'range.back', 'Activity.RangeForward': 'range.forward',
  'Nutrition.GoalOpen': 'diet.goal', 'Nutrition.GoalSave': 'diet.goal.save', 'Nutrition.FoodSearchOpen': 'diet.search',
  'Nutrition.FoodSearch': 'diet.search', 'Nutrition.FoodPick': 'diet.search', 'Nutrition.FoodClear': 'diet.food.clear',
  'Nutrition.ScanMealTap': 'diet.scan', 'Nutrition.ScanMealCapture': 'diet.scan', 'Nutrition.NutrientsOpen': 'diet.nutrients',
  'Nutrition.AddFoodSubmit': 'diet.submit', 'Nutrition.FoodOpen': 'diet.food', 'Nutrition.AddMoreFood': 'diet.more',
  'Nutrition.MealNutrientsOpen': 'diet.nutrients', 'Nutrition.FoodDeleteTap': 'diet.food', 'Nutrition.FoodDeleteConfirm': 'diet.food',
  'Activity.AddOpen': 'activity.add', 'Activity.DetailOpen': 'activity.card', 'Activity.OngoingOpen': 'activity.card',
  'Activity.Search': 'activity.search', 'Games.Play': 'games.play', 'Games.PlayAgain': 'games.again',
  'Consult.BookTap': 'booking.book_now', 'Consult.PastOpen': 'booking.past', 'Consult.DetailOpen': 'booking.card',
  'Consult.GoalToggle': 'booking.goal', 'Consult.ContinueToTime': 'booking.next', 'Consult.DatePick': 'booking.date',
  'Consult.SlotPick': 'booking.slot', 'Consult.MonthBack': 'booking.month.back', 'Consult.MonthForward': 'booking.month.forward',
  'Consult.ConfirmTap': 'booking.book', 'Consult.BackToQuestions': 'back', 'Consult.JoinCall': 'booking.join',
  'Consult.PastDetailOpen': 'booking.card', 'Consult.RatingSubmit': 'booking.submit', 'Consult.RatingSkip': 'booking.skip',
  'Consult.CallEnd': 'booking.end',
  'Booking.StartTap': 'booking.start', 'Booking.Type': 'booking.option', 'Booking.Tier': 'booking.option',
  'Booking.ClinicPick': 'booking.option', 'Booking.JourneyPick': 'booking.option', 'Booking.Duration': 'booking.option',
  'Booking.DatePick': 'booking.date', 'Booking.SlotPick': 'booking.slot', 'Booking.StepBack': 'back', 'Booking.ConfirmTap': 'booking.confirm',
  'Devices.AddTap': 'device.add', 'Devices.ScanStart': 'device.scan', 'Devices.PairTap': 'device.pair', 'Devices.NotMine': 'device.not_mine',
  'Devices.SyncTap': 'device.sync', 'Devices.ForgetTap': 'device.unpair', 'Devices.ForgetConfirm': 'device.unpair',
  'Devices.PriorityOn': 'device.priority', 'Devices.PriorityOff': 'device.priority', 'Devices.PermissionRetry': 'device.scan',
  'Insights.ViewReportsTap': 'insights.reports', 'Insights.ReportOpen': 'results.view', 'Insights.ReportLockedTap': 'results.view',
  'Insights.ReportsBack': 'results.prev', 'Insights.ReportsNext': 'results.next', 'Insights.Metabolome': 'insights.metabolome',
  'Gut.DiversityOpen': 'gut.diversity.title', 'Gut.BalanceOpen': 'gut.balance.title', 'Gut.RiskOpen': 'gut.risk.title',
  'Oral.DiversityOpen': 'oral.diversity.title', 'Oral.BalanceOpen': 'oral.balance.title', 'Oral.RiskOpen': 'oral.risk.title',
  'Genome.TraitOpen': 'genomics.traits.title', 'Genome.TraitPanelTap': 'genomics.traits.title',
  'Genome.DrugOpen': 'genomics.panels.drug', 'Genome.DrugCategory': 'genomics.panels.drug',
  'Reports.Open': 'reports.open', 'Reports.PendingTap': 'reports.open', 'Reports.PdfGenerate': 'reports.generate',
  'Reports.PdfLanguagePick': 'reports.language', 'Reports.PdfOpenReady': 'reports.generate', 'Reports.PdfFastScroll': 'reports.pages',
  'Clinical.UploadLabReportTap': 'clinical.upload', 'Clinical.LabReportPick': 'clinical.upload',
  'Clinical.FieldTap': 'clinical.values', 'Clinical.ValueEdit': 'clinical.values',
  'AI.NewChat': 'ai.new', 'AI.ConversationOpen': 'ai.history.row', 'AI.VoiceClose': 'ai.voice.close',
  'AI.VoiceSpeakerToggle': 'ai.voice.speaker', 'AI.VoiceMute': 'ai.voice.mic', 'AI.VoiceUnmute': 'ai.voice.mic',
  'Store.Search': 'store.search', 'Store.ProductOpen': 'store.product', 'Store.CartOpen': 'store.cart',
  'Store.CategoryAll': 'store.category.all', 'Store.CategoryPick': 'store.category', 'Store.ProductImage': 'store.image',
  'Store.RemoveFromCart': 'store.minus', 'Store.CartOpenFromProduct': 'store.cart',
  'Cart.Remove': 'cart.remove', 'Cart.CheckoutTap': 'cart.checkout', 'Cart.QuantityUp': 'cart.plus', 'Cart.QuantityDown': 'cart.minus',
  'Checkout.AddressPick': 'checkout.address', 'Checkout.AddressContinue': 'checkout.continue',
  'Profile.PersonalDetails': 'profile.card', 'Profile.PersonalDetailsSave': 'profile.save', 'Profile.DeleteAccountTap': 'profile.delete',
  'Profile.HealthHistorySave': 'profile.save', 'Profile.LifestyleSave': 'profile.save', 'Profile.PasswordChangeSubmit': 'profile.save',
  'Profile.AddressPick': 'profile.address', 'Profile.AddressSave': 'profile.save', 'Kit.CodeSubmit': 'profile.activate',
  'Orders.DetailOpen': 'orders.detail', 'Tests.TrackingOpen': 'tests.tracking',
  'Auth.SignIn': 'signin.signin', 'Register.Step': 'signin.join',
};
const NAV_TAPS = { Home: 'nav.home', Insights: 'nav.insights', LongevityAI: 'nav.ai', Store: 'nav.store', Profile: 'nav.profile' };
const OMICS_CARDS = {
  WholeGenome: 'insights.genome', GutMicrobiomeTest: 'insights.gut', OralMicrobiomeTest: 'insights.oral',
  Metabolome: 'insights.metabolome', Transcriptome: 'insights.transcriptome', Proteome: 'insights.proteome',
  Proteomics: 'insights.proteome', Lipidome: 'insights.lipidome', ClinicalInsights: 'insights.clinical',
  PrecisionOncology: 'insights.oncology', InfectiousDiseases: 'insights.infectious',
};

export function tapTargetOf(step) {
  if (step.kind !== 'event') return null;
  const nav = NAV_PRESS.exec(step.name);
  if (nav) return NAV_TAPS[nav[1]] || null;
  // Sleep, calories and steps are rings at the top of Home; every metric also has
  // its row lower down. Pressed from another tab, the Home tab came first.
  const pressed = METRIC_PRESS.exec(step.name);
  if (pressed) return [`home.${pressed[1]}`, `home.metric.${pressed[1]}`, 'nav.home'];
  if (TAP_TARGETS[step.name]) return TAP_TARGETS[step.name];
  if (!step.name.includes('.')) return null;
  const { area, action, value } = tapParts(step.name);
  if (area === 'Open' || area === 'App' || area === 'Push') return null;
  if (area === 'Profile' && PROFILE_ROWS[action]) return action === 'PersonalDetails' ? 'profile.card' : PROFILE_ROWS[action];
  if (TAP_TARGETS[`${area}.${action}`]) return TAP_TARGETS[`${area}.${action}`];
  if (action === 'PdfTap') return 'omics.pdf';
  if (/Back$/.test(action)) return 'back';
  if (value === null) return null;
  if (/^(Health\.Period|Habits\.Range|Nutrition\.Range|Activity\.Range)$/.test(`${area}.${action}`)) return `range.${value}`;
  if (area === 'Insights' && action === 'Open') return OMICS_CARDS[value] || null;
  if (area === 'Genome' && action === 'Panel') return PANELS[value] || null;
  if (area === 'Genome' && action === 'ViewAll') return /trait/i.test(value) ? 'genomics.traits.title' : /drug/i.test(value) ? 'genomics.panels.drug' : null;
  if (area === 'Nutrition' && (action === 'AddMealOpen' || action === 'MealOpen')) return `diet.${value.toLowerCase()}`;
  if (area === 'Activity' && action === 'Pick') return [`activity.pick.${value}`, 'activity.pick.any'];
  if (area === 'Games' && action === 'LeaderboardOpen') return `games.${value}`;
  if (area === 'Consult' && (action === 'Type' || action === 'Clinician')) return [`booking.${snake(action)}.${value}`, 'booking.option'];
  if (area === 'Consult' && action === 'RatingStars') return `booking.star.${value}`;
  if (area === 'Devices' && action === 'DetailOpen') return [`device.card.${value}`, 'device.card'];
  if (area === 'Devices' && action === 'Pick') return `device.pick.${value}`;
  if (area === 'AI' && action === 'SuggestedPrompt') return `ai.prompt.${value}`;
  if (area === 'Settings' && action === 'Appearance') return `profile.appearance.${value}`;
  if (area === 'Settings' && action === 'Language') return [`profile.language.${value}`, 'profile.language.any'];
  return null;
}

// Where the press that opens a page sits on the page before it, for pages
// opened without that press logged: builds before 5.4 log few presses, and a
// press and its page can arrive out of order. The first spot drawn wins.
const ENTRY = {
  'home:null': ['nav.home'], 'insights:null': ['nav.insights'], 'store:store': ['nav.store'], 'profile:null': ['nav.profile'], 'ai:chat': ['nav.ai'],
  'device:list': ['home.device.all', 'home.device.add'], 'device:detail': ['device.card', 'home.device.tile'],
  'device:add': ['device.add', 'home.device.add'], 'device:scan': ['device.pick.ring', 'device.pick.strap'], 'device:found': ['device.scan'],
  'device:paired': ['device.pair', 'home.device.add'],
  'habits:list': ['home.habits'], 'habits:detail': ['habits.detail'], 'habits:add': ['habits.add'], 'habits:edit': ['habits.edit'],
  'diet:tracker': ['home.diet'], 'diet:add': ['diet.breakfast', 'diet.more'], 'diet:meal': ['diet.breakfast'], 'diet:goal': ['diet.goal'],
  'activity:list': ['home.activity'], 'activity:picker': ['activity.add', 'home.activity.add'],
  'activity:timer': ['activity.pick.any', 'activity.card', 'home.activity.add'],
  'activity:detail': ['activity.card'], 'games:list': ['home.games'],
  'booking:list': ['home.consult'], 'booking:flow': ['booking.book_now'], 'booking:past': ['booking.past'], 'booking:detail': ['booking.card'],
  'booking:call': ['booking.join'], 'booking:clinic': ['home.consult.book'], 'booking:clinic_flow': ['booking.start', 'home.consult.book'],
  'metric:capacity': ['home.capacity'],
  'reports:inbox': ['insights.reports'], 'reports:viewer': ['reports.open'], 'reports:request': ['omics.pdf'], 'clinical:null': ['insights.clinical'],
  'store:product': ['store.product'], 'store:cart': ['store.cart'], 'checkout:null': ['cart.checkout'],
  'orders:null': ['profile.menu.order_history'], 'tests:null': ['profile.menu.past_tests'], 'ai:history': ['ai.history'], 'ai:voice': ['ai.voice'],
  'profile:personal_details': ['profile.card'],
};
const pageOf = (visit) => `${visit.screen}:${visit.part}`;

function entryKeys(visit) {
  if (ENTRY[pageOf(visit)]) return ENTRY[pageOf(visit)];
  const part = visit.part || '';
  switch (visit.screen) {
    case 'metric': return [`home.${part}`, `home.metric.${part}`];
    case 'results': return [`insights.${part}`];
    case 'omics': return ['results.view'];
    case 'profile': return part ? [`profile.menu.${part}`] : [];
    case 'games': return part.startsWith('board.') ? [`games.${part.slice(6)}`] : part.startsWith('play.') ? ['games.play'] : [];
    default: return [];
  }
}

// The presses to show while a visit is on the phone: its own steps (the one
// that opened it only when it was made here), then how the person left it:
// back, which the app does not log; the press that opened the next page; or,
// when nothing logged that press, the spot that opens it. The phone skips any
// it has no spot for, and a long visit keeps its first presses and its last.
const MOST_PRESSES = 6;
// Presses after which the app closes the page by itself, so leaving it needs no back press.
const CLOSES_PAGE = new Set([
  'Habits.AddSubmit', 'Habits.EditSave', 'Habits.DeleteConfirm', 'Nutrition.AddFoodSubmit', 'Consult.RatingSkip', 'AI.VoiceClose',
  'Devices.ForgetConfirm', 'Devices.NotMine', 'Profile.PasswordChangeSubmit', 'Checkout.AddressContinue',
]);

export function visitTaps(visit, next) {
  const taps = [];
  for (const step of visit.steps.filter((entry, index) => index > 0 || !screenOfStep(entry).opens)) {
    const found = tapTargetOf(step);
    if (found === null) continue;
    const key = Array.isArray(found) ? [...found] : [found];
    // A press that opens a page is made on the page before it; with no such
    // spot here, the person went back first.
    if (screenOfStep(step).opens && !key.includes('back')) key.push('back');
    if (taps.length > 0 && taps[taps.length - 1].key.join(' ') === key.join(' ')) continue;
    taps.push({ key, moment: momentOf(step), leaving: false });
  }
  const kept = taps.length > MOST_PRESSES - 1 ? [...taps.slice(0, MOST_PRESSES - 2), taps[taps.length - 1]] : taps;
  if (!next) return kept;
  const first = next.steps[0];
  const lastPress = [...visit.steps].reverse().find((step) => step.kind === 'event' && !/^(App|Open|Push)\./.test(step.name));
  const closed = Boolean(lastPress) && CLOSES_PAGE.has(lastPress.name.split('.').slice(0, 2).join('.'));
  if (next.back) {
    if (!closed) kept.push({ key: ['back'], moment: null, leaving: true });
  } else if (first && screenOfStep(first).opens) {
    const found = tapTargetOf(first);
    if (found !== null) kept.push({ key: [...(Array.isArray(found) ? found : [found]), 'back'], moment: momentOf(first), leaving: true });
  } else if (!closed) {
    const entry = entryKeys(next);
    const last = kept[kept.length - 1];
    // This page's last press opened the next one; or nothing logged that press,
    // and the phone presses the spot that opens it, or back without one.
    if (last && last.key.some((key) => entry.includes(key))) last.leaving = true;
    else if (entry.length > 0) kept.push({ key: [...entry, 'back'], moment: null, leaving: true });
  }
  return kept;
}

// The app sends a press and the page it opens as two events at the same moment,
// and they can reach the server in either order. A page that shows up just
// before the press that opened it is put back after that press.
const RACE_MS = 1500;
// Events that are a page appearing, not a press.
const APPEARS = new Set(['DevicePaired', 'BookingFlowOpen', 'PastTestsOpen', 'OrderHistoryOpen', 'ClinicalInsightsOpen']);

function inOrder(steps) {
  const ordered = [...steps];
  for (let index = 1; index < ordered.length; index += 1) {
    const step = ordered[index];
    const opened = ordered[index - 1];
    if (step.kind !== 'event' || opened.kind !== 'event' || !opened.name.startsWith('Open.') || step.name.startsWith('Open.')
      || APPEARS.has(step.name) || step.at - opened.at > RACE_MS) continue;
    const place = screenOfStep(step);
    const page = screenOfStep(opened);
    if (!place.screen || !page.screen) continue;
    const same = place.screen === page.screen && (place.part === undefined || place.part === page.part);
    // A press opening this very page, or one made on another page, came first.
    if (place.opens ? same : !same) {
      ordered[index - 1] = step;
      ordered[index] = opened;
    }
  }
  return ordered;
}

// A session as the screens it went through. Waking the app again mid-session
// belongs to the screen it was on, so a screen reloading stays one visit. Once
// the app names its screens (5.4), what loads no longer decides the screen.
export function sessionVisits(session) {
  const named = session.steps.some((step) => step.kind === 'event' && step.name.startsWith('Open.'));
  const visits = [];
  let waking = false;
  for (const step of named ? inOrder(session.steps) : session.steps) {
    const found = screenOfStep(step);
    const screen = named && step.kind !== 'event' && found.screen !== 'launch' && found.screen !== 'signin' ? null : found.screen;
    const { part } = found;
    // The app coming back: from 5.4 it says so, before that it is one burst of start-up calls.
    const wakes = named ? step.kind === 'event' && step.name === 'App.Resume' : screen === 'launch' && !waking;
    waking = screen === 'launch';
    const last = visits[visits.length - 1];
    if (last !== undefined && (screen === null || screen === 'launch'
      || (last.screen === screen && (part === undefined || last.part === part)))) {
      if (wakes && last.screen !== 'launch') last.reopened += 1;
      last.steps.push(step);
      last.end = step.at;
      continue;
    }
    const first = screen === null ? 'launch' : screen;
    visits.push({ screen: first, part: part === undefined ? placed(first, null).part : part, start: step.at, end: step.at, steps: [step], reopened: 0 });
  }
  return visits;
}

// Pages open as a stack over a tab, and sheets over the page under them.
const TAB_ROOTS = new Set(['home:null', 'insights:null', 'store:store', 'profile:null', 'ai:chat']);
const isSheet = (page) => page === 'ai:history' || page === 'checkout:null' || page.startsWith('games:board.');
// Pages the app opens in place of the ones under them: a found device replaces
// the scan, pairing replaces both (its Done closes two pages at once), and the
// rating replaces the call and the consultation it closes back past.
const REPLACES = {
  'device:found': ['device:scan'], 'device:scan': ['device:found'], 'device:paired': ['device:found', 'device:add'],
  'booking:rating': ['booking:call', 'booking:detail'],
};

// The page a press that opens another page is made on; 'tab' for the tab bar.
function pressedOn(step) {
  if (NAV_PRESS.test(step.name)) return 'tab';
  if (METRIC_PRESS.test(step.name)) return 'home:null';
  if (step.name === 'PastTestsOpen' || step.name === 'OrderHistoryOpen') return 'profile:null';
  if (step.name === 'ClinicalInsightsOpen') return 'insights:null';
  const { area, action, value } = tapParts(step.name);
  if (area === 'Profile' && PROFILE_ROWS[action]) return 'profile:null';
  switch (`${area}.${action}`) {
    case 'Habits.AddOpen': return 'habits:list';
    case 'Habits.EditOpen': return 'habits:detail';
    case 'Games.LeaderboardOpen': return 'games:list';
    case 'Games.Play': return `games:board.${value}`;
    case 'AI.ConversationOpen': return 'ai:history';
    case 'Consult.ContinueToTime': return 'booking:flow';
    case 'Consult.BackToQuestions': return 'booking:flow_time';
    default: return null;
  }
}

const appears = (step) => step.kind !== 'event' || step.name.startsWith('Open.') || step.name.startsWith('App.') || APPEARS.has(step.name);

function wentBackTo(page, at) {
  const [screen, part] = [page.slice(0, page.indexOf(':')), page.slice(page.indexOf(':') + 1)];
  return { screen, part: part === 'null' ? null : part, start: at, end: at, steps: [], reopened: 0, returned: true, back: true };
}

// A session as the replay shows it. The app does not log going back, so when
// the next press was made on a page further down the stack of open pages, the
// pages the person went back through are put in between (marked returned),
// and a page reached that way is marked back.
export function replayVisits(session) {
  const visits = sessionVisits(session);
  if (!session.steps.some((step) => step.kind === 'event' && step.name.startsWith('Open.'))) return visits;
  const shown = [];
  let stack = [];
  for (const visit of visits) {
    const page = pageOf(visit);
    const first = visit.steps[0];
    if (visit.screen === 'launch' || visit.screen === 'signin' || stack.length === 0 || !first) {
      if (visit.screen !== 'launch') stack = [page];
      shown.push(visit);
      continue;
    }
    const found = screenOfStep(first);
    const on = found.opens ? pressedOn(first) : appears(first) ? null : page;
    let at = on === 'tab' ? (TAB_ROOTS.has(stack[0]) ? 0 : -1) : on === null ? -1 : stack.lastIndexOf(on);
    if (at >= 0 && at < stack.length - 1) {
      for (let index = stack.length - 2; index >= at; index -= 1) {
        // The last page gone back to is this visit itself when the press was made on it.
        if (index === at && (on === page || (on === 'tab' && stack[at] === page))) {
          visit.back = true;
          break;
        }
        shown.push(wentBackTo(stack[index], visit.start));
      }
      stack = stack.slice(0, at + 1);
    }
    if (TAB_ROOTS.has(page)) {
      stack = [page];
    } else if (on === page) {
      const top = stack[stack.length - 1];
      // Another part of the same page (a metric switched, say) takes its place.
      if (top !== page && top.slice(0, top.indexOf(':')) === visit.screen) stack[stack.length - 1] = page;
      else if (top !== page) stack.push(page);
    } else if (found.opens && isSheet(stack[stack.length - 1])) {
      // A press on a sheet closes it: what it opens takes its place, or the page under it shows again.
      stack.pop();
      if (stack[stack.length - 1] !== page) stack.push(page);
    } else {
      while (stack.length > 1 && REPLACES[page] && REPLACES[page].includes(stack[stack.length - 1])) stack.pop();
      stack.push(page);
    }
    shown.push(visit);
  }
  return shown;
}

// What the rest of the session says about the screen on the phone: the meal
// being added, the game being played, the language picked, the devices the
// person has. Values are the latest tapped up to that screen.
const HINTS = {
  'Nutrition.AddMealOpen': 'meal', 'Nutrition.MealOpen': 'meal', 'Activity.Pick': 'activity', 'Games.LeaderboardOpen': 'game',
  'Games.Play': 'game', 'Settings.Language': 'language', 'Settings.Appearance': 'appearance', 'Consult.Type': 'consult',
  'Devices.Pick': 'pick', 'Devices.DetailOpen': 'device',
};
const DEVICE_KINDS = { yc_ring: 'ring', qc_band: 'band', ring: 'ring', strap: 'band' };
// Signs a phone has a device paired: it syncs one, or the person opens one.
const SYNCS = /^\/(Health\/(Submit|DeviceSyncEvent|ShouldSync)|Devices\/sync-token)$/;
const OPENS_DEVICE = /^(Home\.(DeviceTileTap|AllDevicesTap)|Open\.(DeviceDetail|YourDevices)|Devices\.(DetailOpen|SyncTap|ForgetTap))/;
// Signs the person has a consultation booked: they open one or join its call.
const HAS_CONSULTATION = /^(Consult\.(DetailOpen|JoinCall)|Open\.(ConsultDetail|VideoCall))$/;
const OPENS_CART = /^(Store\.(CartOpen|CartOpenFromProduct)|Open\.Cart)$/;

export function screenHints(session, visits, index) {
  const kinds = new Set();
  let pairedAt = null;
  // A device already there before any pairing in the session.
  let paired = false;
  let consulted = false;
  for (const step of session.steps) {
    const before = pairedAt === null;
    if (step.kind !== 'event') {
      if (SYNCS.test(step.name) && before) paired = true;
      continue;
    }
    if (OPENS_DEVICE.test(step.name) && before) paired = true;
    if (HAS_CONSULTATION.test(step.name)) consulted = true;
    if (step.name === 'DevicePaired' && pairedAt === null) pairedAt = step.at;
    const { area, value } = tapParts(step.name);
    if (area === 'Devices' && value !== null && DEVICE_KINDS[value]) kinds.add(DEVICE_KINDS[value]);
  }
  const hints = {};
  let added = 0;
  for (const visit of visits.slice(0, index + 1)) {
    for (const step of visit.steps) {
      if (step.kind !== 'event') continue;
      if (step.name === 'ShopAddToCart') added += 1;
      if (!step.name.includes('.')) continue;
      const { area, action, value } = tapParts(step.name);
      if (value !== null && HINTS[`${area}.${action}`]) hints[HINTS[`${area}.${action}`]] = value;
    }
  }
  // A cart someone opens next had something in it, even when it was filled in an earlier session.
  const opensCart = visits.slice(index, index + 2).some((visit) => visit.steps.some((step) => OPENS_CART.test(step.name)));
  const visit = visits[index];
  // Someone pairing a first device during the session had none on Home before it.
  hints.hasDevice = paired || (pairedAt !== null && Boolean(visit && visit.start >= pairedAt));
  hints.kinds = kinds.size > 0 ? [...kinds].sort() : ['ring'];
  hints.cart = Math.max(added, opensCart ? 1 : 0);
  hints.consulted = consulted;
  return hints;
}

export function metricName(metric, as) {
  const keys = {
    sleep: 'homepage.sleep_label', steps: 'homepage.steps_label', calories: 'homepage.calories_label',
    heartRate: 'homepage.heart_rate_label', heartRateAvg: 'homepage.heart_rate_label', restingHeartRate: 'homepage.resting_heart_rate',
    bloodOxygen: 'homepage.blood_oxygen', bloodPressure: 'homepage.blood_pressure', respiratoryRate: 'homepage.respiratory_rate',
    distance: 'homepage.step_distance', hrv: 'health.variability_heart_rate', capacity: 'homepage.capacity',
    sleepConsistency: 'health.sleep.consistency', sleepEfficiency: 'health.sleep.efficiency',
    hoursVsNeeds: 'health.sleep.hours_vs_needs', restorativeSleep: 'health.sleep.restorative_sleep',
  };
  return keys[metric] ? as(keys[metric]) : metric;
}

// Each omics screen of the app: its title, the line under it and the parts it
// opens onto, in the app's words.
export const OMICS_SECTIONS = {
  genome: {
    title: 'genomics.title', subtitle: 'omics.genome.subtitle',
    parts: ['genomics.panels.health_conditions', 'genomics.panels.carrier_risk', 'genomics.panels.newborn',
      'genomics.panels.hereditary_cancer', 'genomics.panels.drug', 'genomics.traits.title'],
  },
  gut: { title: 'gut.title', subtitle: 'omics.gut.subtitle', parts: ['gut.diversity.title', 'gut.balance.title', 'gut.risk.title'] },
  oral: { title: 'oral.title', subtitle: 'omics.oral.subtitle', parts: ['oral.diversity.title', 'oral.balance.title', 'oral.risk.title'] },
  carrier: { title: 'carrier.title', subtitle: 'carrier.subtitle', parts: [] },
  newborn: { title: 'newborn.title', subtitle: 'newborn.subtitle', parts: [] },
  sports: { title: 'sports.title', subtitle: 'sports.subtitle', parts: [] },
  proteome: { title: 'proteomics.title', subtitle: 'proteomics.subtitle', parts: [] },
  transcriptome: { title: 'transcriptome.title', subtitle: 'transcriptome.subtitle', parts: ['transcriptome.summary', 'transcriptome.recommendations'] },
  lipidome: { title: 'lipidome.title', subtitle: 'lipidome.header.subtitle', parts: [] },
  metabolome: { title: 'omics.metabolome.title', subtitle: 'omics.metabolome.subtitle', parts: ['urine.title', 'plasma.title'] },
  urine: { title: 'urine.title', subtitle: 'omics.metabolome.subtitle', parts: [] },
  plasma: { title: 'plasma.title', subtitle: 'omics.metabolome.subtitle', parts: [] },
};

// The tile that leads to each list of results, as the Insights tab names it.
export const RESULT_TITLES = {
  genome: 'omics.genome.title', gut: 'omics.gut.title', oral: 'omics.oral.title', transcriptome: 'omics.transcriptome.title',
  proteome: 'omics.proteome.title', lipidome: 'omics.lipidome.title', metabolome: 'omics.metabolome.title',
  urine: 'omics.metabolome.title', plasma: 'omics.metabolome.title', oncology: 'omics.oncology.title', infectious: 'omics.infectious.title',
};

// Each page by the title the app gives it.
const PART_TITLES = {
  device: { list: 'device.your_devices', detail: 'device.device_settings', add: 'device.add_device', found: 'device.device_found', paired: 'device.your_device_is_paired' },
  habits: { list: 'habits.header', add: 'habits.add_habits', edit: 'habits.edit_habits' },
  diet: { tracker: 'nutrition.diet_tracker_header', add: 'nutrition.add_meal', meal: 'nutrition.your_meal', goal: 'nutrition.set_your_goal' },
  activity: { list: 'activity.title', picker: 'activity.select_activity' },
  booking: {
    list: 'consultation.header', flow: 'consultation.questionnaire_title', flow_time: 'consultation.date_time_title',
    detail: 'consultation.details_title', past: 'consultation.past_consultations',
    rating: 'consultation.rate_experience', clinic: 'homepage.consultations_title', clinic_flow: 'consultation.book_consultation',
  },
  store: { store: 'store.title', product: 'store.product_detail', cart: 'store.your_cart' },
  ai: { chat: 'navbar.longevity_ai', history: 'ai.history.title' },
  reports: { inbox: 'reports.title', request: 'common.generate_pdf', viewer: 'reports.type.health_pdf' },
  games: { list: 'games.title' },
};
// Pages whose own title would not tell them apart; the portal names these.
const PART_LABELS = {
  'device:scan': 'ui.mobile.screen_device_scan', 'habits:detail': 'ui.mobile.screen_habit_detail',
  'activity:timer': 'ui.mobile.screen_activity_timer', 'activity:detail': 'ui.mobile.screen_activity_detail',
  'booking:call': 'ui.mobile.screen_video_call', 'ai:voice': 'ui.mobile.screen_voice',
};

export function screenName(visit, as) {
  const part = visit.part || FIRST_PARTS[visit.screen] || null;
  if (PART_LABELS[`${visit.screen}:${part}`]) return tx(PART_LABELS[`${visit.screen}:${part}`]);
  if (PART_TITLES[visit.screen] && PART_TITLES[visit.screen][part]) return as(PART_TITLES[visit.screen][part]);
  switch (visit.screen) {
    case 'launch': return tx('ui.mobile.screen_launch');
    case 'signin': return tx('ui.mobile.screen_signin');
    case 'home': return as('navbar.home');
    case 'insights': return as('navbar.insights');
    case 'results': return as(RESULT_TITLES[part] || 'omics.results_title');
    case 'clinical': return as('omics.clinical.title');
    case 'checkout': return as('store.delivery_address');
    case 'orders': return as('profile.orders.title');
    case 'profile': return as(part ? `profile.menu.${part}` : 'navbar.profile');
    case 'tests': return as('profile.tests.title');
    case 'metric': return metricName(part, as);
    case 'omics': return as(OMICS_SECTIONS[part] ? OMICS_SECTIONS[part].title : 'omics.title');
    case 'games': {
      const [page, game] = (part || '').split('.');
      if (game && page === 'board') return tx('ui.mobile.screen_game_scores', { game: as(`games.${game}`) });
      if (game) return tx('ui.mobile.screen_game_play', { game: as(`games.${game}`) });
      return as('games.title');
    }
    default: return visit.screen;
  }
}

// ---------------------------------------------------------------- the phone

// The Phenome Longevity mark from the app bar, the app's own paths.
export function AppLogo({ color = APP_BLUE, height = 24 }) {
  return (
    <svg viewBox="0 0 33 22" height={height} width={height * 1.5} aria-hidden="true" fill={color}>
      <path d="M21.3642 21.5192H16.6861V0.1073H25.6163C27.4078 0.1073 29.0184 0.784106 30.5064 2.10409C31.9944 3.42407 32.753 5.17494 32.753 7.3567C32.753 9.53845 32.0235 11.1653 30.5667 12.5484C29.1078 13.9314 27.468 14.6061 25.6163 14.6061H21.3642V21.5171V21.5192ZM21.3642 4.40775V10.3056H25.342C25.98 10.3056 26.618 10.0912 27.1958 9.62883C27.7735 9.16852 28.077 8.39923 28.077 7.3546C28.077 6.30996 27.7735 5.5722 27.1958 5.11189C26.618 4.65157 25.98 4.40565 25.342 4.40565H21.3642V4.40775Z" />
      <path d="M11.0935 9.48387C11.0062 9.4019 10.9252 9.32833 10.8462 9.25056C10.6197 9.02566 10.3537 8.87222 10.044 8.79655C9.81125 8.7398 9.57433 8.7335 9.33533 8.75662C9.0132 8.78815 8.72225 8.89744 8.46662 9.09712C8.3731 9.17069 8.28789 9.25266 8.20476 9.33674C7.97408 9.56795 7.70391 9.7361 7.38593 9.80756C6.74376 9.95469 6.17847 9.80756 5.70255 9.34304C5.61527 9.25687 5.52798 9.16859 5.4303 9.09502C5.17468 8.89744 4.88373 8.78815 4.5616 8.75662C4.3226 8.7335 4.08776 8.74191 3.85499 8.79866C3.54118 8.87432 3.27308 9.03407 3.04448 9.26317C3.0133 9.2947 2.98213 9.32833 2.94888 9.35986C2.61844 9.68565 2.21941 9.85801 1.75804 9.86431C.87063 9.87482.209747 9.25056.0434876 8.517C-.168494 7.58167.415494 6.64843 1.33824 6.44244C1.95963 6.30372 2.50413 6.46767 2.96342 6.91116C3.03409 6.97842 3.10267 7.05199 3.17748 7.11505C3.48091 7.36937 3.83213 7.49549 4.22492 7.5102C4.43482 7.51651 4.64265 7.5081 4.84632 7.45135C5.13727 7.37148 5.38874 7.22434 5.60488 7.01416C5.64229 6.97632 5.67969 6.93639 5.7171 6.90065C6.0517 6.57486 6.45072 6.40461 6.92041 6.3962C7.43997 6.3878 7.87848 6.57486 8.23802 6.9511C8.49156 7.21594 8.79083 7.40301 9.15244 7.47027C9.55355 7.54593 9.95049 7.52912 10.3329 7.36097C10.549 7.26638 10.7361 7.12766 10.8982 6.9553C11.0748 6.76613 11.2785 6.619 11.5175 6.52232C12.5358 6.11035 13.6664 6.71569 13.8742 7.80447C14.0592 8.77343 13.4129 9.64782 12.5275 9.82438C12.0038 9.92947 11.53 9.81387 11.0977 9.48597L11.0935 9.48387Z" />
      <path d="M10.601 20.42C10.4908 20.1762 10.4347 19.9303 10.441 19.6717C10.4493 19.2177 10.6093 18.8247 10.919 18.4926C10.9522 18.4568 10.9896 18.4232 11.0249 18.3896C11.3616 18.0575 11.5404 17.656 11.5507 17.181C11.5549 16.996 11.5507 16.8111 11.5092 16.6303C11.4344 16.3024 11.2723 16.0207 11.0353 15.7832C10.9771 15.7244 10.919 15.6676 10.8608 15.6088C10.5199 15.2683 10.1084 15.0959 9.62628 15.0896C9.43301 15.0875 9.24181 15.098 9.05477 15.1485C8.76797 15.2262 8.51858 15.3713 8.3066 15.5773C8.26919 15.6151 8.23178 15.6529 8.19437 15.6908C7.79535 16.0817 7.31527 16.2541 6.76246 16.1931C5.9852 16.1048 5.38666 15.5142 5.25781 14.7659C5.1061 13.8684 5.6506 13.0172 6.53177 12.7944C7.15941 12.6346 7.71222 12.7944 8.18398 13.24C8.21931 13.2736 8.25256 13.3114 8.28789 13.3451C8.58093 13.6351 8.93215 13.8033 9.34364 13.8411C9.58264 13.8642 9.81748 13.8558 10.0502 13.7991C10.3765 13.7192 10.6529 13.551 10.8857 13.3072C10.9959 13.1916 11.1164 13.0844 11.2515 13.0003C12.2657 12.3677 13.629 12.9142 13.868 14.1459C13.9864 14.7512 13.8098 15.283 13.3692 15.7181C13.2071 15.8778 13.0637 16.0481 12.964 16.2541C12.8621 16.4621 12.8018 16.6828 12.7935 16.9162C12.7852 17.1305 12.7873 17.3428 12.8372 17.553C12.912 17.8557 13.0575 18.1163 13.2695 18.3412C13.3089 18.3833 13.3526 18.4211 13.3921 18.4631C13.7578 18.8394 13.9324 19.2913 13.895 19.8147C13.8306 20.7037 13.1489 21.2923 12.4444 21.4037C11.6484 21.5277 10.9127 21.0947 10.5989 20.42H10.601Z" />
      <path d="M11.0976 3.10041C11.0104 3.01844 10.9293 2.94487 10.8503 2.8671C10.6363 2.65481 10.389 2.50137 10.0959 2.4236C9.71977 2.32482 9.34153 2.32902 8.96952 2.44252C8.70974 2.52239 8.48737 2.66532 8.29409 2.85449C8.24629 2.90073 8.20472 2.94908 8.15485 2.99322C7.6623 3.43881 7.09286 3.58595 6.459 3.39257C5.82513 3.1992 5.42611 2.75991 5.2744 2.11253C5.06449 1.21712 5.61315.281785 6.5442.0547811C7.15313-.0923508 7.69348.0589848 8.15692.483565C8.19018.512992 8.22135.546622 8.25252.580252C8.51438.851395 8.82612 1.03006 9.19813 1.08891C9.61585 1.15617 10.0232 1.12674 10.4056.931267C10.5718.847191 10.7173.735792 10.8503.605475C10.9231.534011 10.9937.458343 11.0727.393184C12.0869-.42655 13.5708.107328 13.8534 1.35795C14.0591 2.26386 13.5043 3.22442 12.5254 3.43251C12.0058 3.54391 11.5258 3.43041 11.0935 3.09831L11.0976 3.10041Z" />
      <path d="M1.36944 12.704C1.31956 12.1007 1.51699 11.6047 1.95966 11.2116C2.27556 10.9321 2.65172 10.7913 3.07152 10.7892C3.99634 10.7829 4.66346 11.4618 4.79231 12.2163C4.95649 13.1643 4.34965 14.0324 3.45392 14.2215C2.43766 14.4359 1.56064 13.7507 1.3923 12.8637C1.38399 12.8154 1.37775 12.767 1.36944 12.7082V12.704Z" />
      <path d="M3.13796 1.99275C3.58686 2.01587 3.97134 2.17141 4.28723 2.47618C4.62183 2.80197 4.80056 3.19923 4.81926 3.66795C4.85251 4.49399 4.28723 5.24857 3.45178 5.42512C2.46253 5.63321 1.57512 4.97532 1.39639 4.07361C1.21143 3.14038 1.79126 2.2744 2.65581 2.0495C2.81168 2.00956 2.96962 1.98854 3.14004 1.99275H3.13796Z" />
    </svg>
  );
}

function StatusIcons() {
  return (
    <span className="xo-app-status-icons" aria-hidden="true">
      <svg viewBox="0 0 18 12"><rect x="0" y="8" width="3" height="4" rx=".8" /><rect x="5" y="5.5" width="3" height="6.5" rx=".8" /><rect x="10" y="3" width="3" height="9" rx=".8" /><rect x="15" y="0" width="3" height="12" rx=".8" /></svg>
      <svg viewBox="0 0 16 12"><path d="M8 11.6 5.7 9.2a3.3 3.3 0 0 1 4.6 0L8 11.6Zm-3.9-4.1L2.5 5.8a7.8 7.8 0 0 1 11 0l-1.6 1.7a5.5 5.5 0 0 0-7.8 0ZM.8 4 0 3.2a11.3 11.3 0 0 1 16 0l-.8.8-.9.9a9.9 9.9 0 0 0-12.6 0L.8 4Z" /></svg>
      <svg viewBox="0 0 27 12"><rect x=".5" y=".5" width="23" height="11" rx="3.2" fill="none" stroke="currentColor" opacity=".45" /><rect x="2" y="2" width="17" height="8" rx="1.8" /><rect x="24.6" y="4" width="1.8" height="4" rx=".9" opacity=".45" /></svg>
    </span>
  );
}

// An iPhone or an Android phone, by the platform the session ran on. The
// screen inside is drawn at the app's own size and scaled to fit.
export function AppPhone({ platform = 'ios', clock = '9:41', dark = false, scale = 0.7, children, className = '' }) {
  const android = platform === 'android';
  return (
    <div className={`xo-app-phone${android ? ' is-android' : ' is-ios'}${dark ? ' is-dark' : ''} ${className}`}
      style={{ '--xo-app-scale': scale }}>
      <div className="xo-app-glass">
        <div className="xo-app-canvas">
          <div className="xo-app-statusbar">
            <span className="xo-app-clock">{clock}</span>
            {android ? <i className="xo-app-punch" aria-hidden="true" /> : <i className="xo-app-island" aria-hidden="true" />}
            <StatusIcons />
          </div>
          {children}
          <i className="xo-app-home-bar" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

// What a step did on its screen, so the phone can show it happening.
export function momentOf(step) {
  if (!step) return null;
  switch (step.name) {
    case 'AiChatMessage': case '/AI/text': case '/AI/conversations/:id': case 'AI.ConversationOpen': return 'chat';
    case 'ShopAddToCart': return 'added';
    case 'HabitToggle': case '/Habits/toggle': case 'Habits.CompleteFromDetail': return 'ticked';
    case 'ActivityStart': case '/Activity/start': case '/Activity/current': return 'running';
    case 'BookingConfirmed': case 'BookConsultationPress': case '/Bookings/book': return 'booked';
    case 'ShopPaymentSuccess': return 'paid';
    default: return null;
  }
}

// How far past the visible part of a page a spot may sit before the thumb
// scrolls to it, in the app's pixels: the bottom of every page sits under the
// tab bar or the home indicator.
const SAFE_BOTTOM = 130;
const SAFE_TOP = 24;
// One press at normal speed, in milliseconds: scrolling to the spot when it is
// out of sight, the fingertip's glide, holding the press, and a beat after it.
// Faster replays shorten them, up to twice.
const SCROLL_MS = 220;
const GLIDE_MS = 190;
const HOLD_MS = 170;
const AFTER_MS = 60;
// The fingertip stays over the screen between presses and lifts away once
// nothing more is pressed for a moment.
const IDLE_MS = 700;

// A fingertip over the phone: it scrolls to the spot a step pressed when that
// spot is out of sight, glides there, and only then presses it, with a ripple
// from under it. Coming back after a rest it fades in where it lifted away and
// glides on from there; nothing ripples until it presses. Put inside AppPhone,
// over the screen. `tap` is { key, serial }, `key` the spot's marks in order of
// preference; a new serial presses again, even the same spot. `onDone(serial)`
// is called once the press is over, or at once when the screen has no such
// spot, so the replay moves on only after it.
export function TapLayer({ tap, pace = 1, onDone = null }) {
  const layer = useRef(null);
  const finger = useAnimationControls();
  const [press, setPress] = useState(null);
  // Whether the fingertip has been over this phone yet.
  const placed = useRef(false);
  const still = useReducedMotion();
  const quick = Math.min(2, Math.max(1, pace));
  const done = useRef(onDone);
  useEffect(() => { done.current = onDone; }, [onDone]);

  // The press and ripple animations in the stylesheet run at the same pace.
  useEffect(() => {
    if (layer.current) layer.current.parentElement.style.setProperty('--xo-app-pace', String(quick));
  }, [quick]);

  useEffect(() => {
    if (!tap || !layer.current) return undefined;
    let live = true;
    let frame = 0;
    let target = null;
    const timers = [];
    const wait = (ms) => new Promise((resolve) => { timers.push(window.setTimeout(resolve, ms)); });
    const finish = () => { if (done.current) done.current(tap.serial); };
    const canvas = layer.current.parentElement;
    // The phone is drawn at the app's size and scaled down; measure in the app's pixels.
    const scale = canvas.getBoundingClientRect().width / canvas.offsetWidth;
    // The page scrolls on its own clock, so the spot is measured only once it has stopped.
    const scrollPage = (page, to, ms) => new Promise((resolve) => {
      const from = page.scrollTop;
      const began = performance.now();
      const step = (now) => {
        if (!live) return;
        const share = ms <= 0 ? 1 : Math.min(1, (now - began) / ms);
        page.scrollTop = from + (to - from) * (1 - (1 - share) ** 3);
        if (share < 1) frame = window.requestAnimationFrame(step);
        else resolve();
      };
      frame = window.requestAnimationFrame(step);
    });
    (async () => {
      for (const key of Array.isArray(tap.key) ? tap.key : [tap.key]) {
        target = canvas.querySelector(`[data-tap~="${key}"]`);
        if (target) break;
      }
      if (!target) {
        finish();
        return;
      }
      const page = target.closest('.xo-app-body');
      if (page) {
        const view = page.getBoundingClientRect();
        const rect = target.getBoundingClientRect();
        const below = (rect.bottom - view.bottom) / scale + SAFE_BOTTOM;
        const above = (view.top - rect.top) / scale + SAFE_TOP;
        const by = below > 0 ? below : above > 0 ? -above : 0;
        if (by !== 0) await scrollPage(page, Math.max(0, Math.min(page.scrollHeight - page.clientHeight, page.scrollTop + by)), still ? 0 : SCROLL_MS / quick);
      }
      if (!live) return;
      const box = canvas.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      const spot = { x: (rect.left + rect.width / 2 - box.left) / scale, y: (rect.top + rect.height / 2 - box.top) / scale };
      // The first time it comes in from just short of the spot; after that it
      // stays where it lifted away, faded out, and glides on from there.
      if (!placed.current) finger.set({ x: spot.x + 22, y: spot.y + 56, opacity: 0 });
      placed.current = true;
      finger.start({
        x: spot.x, y: spot.y, opacity: 1,
        transition: still ? { duration: 0 } : { type: 'spring', stiffness: 680 * quick * quick, damping: 48 * quick, opacity: { duration: 0.14 / quick } },
      });
      await wait(still ? 0 : GLIDE_MS / quick);
      if (!live) return;
      target.classList.add('is-tapped');
      setPress({ ...spot, serial: tap.serial });
      await wait(still ? 200 : HOLD_MS / quick);
      if (!live) return;
      target.classList.remove('is-tapped');
      await wait(AFTER_MS / quick);
      if (!live) return;
      finish();
      // Lifting away it drifts off the spot as it fades, so coming back later it
      // is not seen over the button it pressed; off the tab bar, below it.
      await wait((IDLE_MS - AFTER_MS) / quick);
      if (!live) return;
      finger.start({ x: spot.x + (spot.y > 700 ? 8 : 16), y: spot.y + (spot.y > 700 ? 60 : 34), opacity: 0, transition: { duration: 0.3, ease: 'easeOut' } });
    })();
    return () => {
      live = false;
      timers.forEach((timer) => window.clearTimeout(timer));
      window.cancelAnimationFrame(frame);
      if (target) target.classList.remove('is-tapped');
    };
  }, [tap, still, quick, finger]);

  return (
    <div className="xo-app-taps" ref={layer} aria-hidden="true">
      <motion.span className="xo-app-finger" initial={{ opacity: 0 }} animate={finger}>
        <i key={press ? press.serial : 'none'} className={press ? 'is-pressing' : ''} />
      </motion.span>
      {press && !still && <span key={press.serial} className="xo-app-ripple" style={{ left: press.x, top: press.y }} />}
    </div>
  );
}

// ---------------------------------------------------------------- the lock screen

// The phone locked, with the pushes that reached it, as iOS or Android shows them.
export function LockScreen({ notifications, at = Date.now(), platform = 'ios', appName, scale = 0.7 }) {
  const date = new Date(at);
  return (
    <AppPhone platform={platform} dark scale={scale} clock={date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}>
      <div className="xo-app-lock">
        <small>{date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</small>
        <strong>{date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, '')}</strong>
        <div className="xo-app-lock-stack">
          {notifications.map((note, index) => (
            <motion.div key={note.id} className="xo-app-banner-note" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.12, duration: 0.3 }}>
              <span className="xo-app-note-icon"><AppLogo color="#fff" height={14} /></span>
              <span className="xo-app-note-text">
                <span className="xo-app-note-head"><b>{appName}</b><em>{note.when}</em></span>
                <strong>{note.title}</strong>
                <small>{note.body}</small>
              </span>
            </motion.div>
          ))}
        </div>
        <div className="xo-app-lock-foot"><span /><span /></div>
      </div>
    </AppPhone>
  );
}
